import { ChevronDown } from 'lucide-react'

import {
  NetworkDisplay,
  NetworkLogo,
  Tooltip,
} from '~/components/component-library'
import {
  TabsList,
  TabsRoot,
  TabsTrigger,
} from '~/components/component-library/Tabs'
import { useChainTabsOverflow } from '~/components/component-library/TokenSelector/useChainTabsOverflow'
import { RelayNetworkMetadata } from '~/hooks/useRelayNetworks'

type TokenSelectorNetworkTabsProps = {
  activeChainIds: number[]
  selectedChainFilter: number | null
  selectedRelayNetwork: RelayNetworkMetadata | null
  allowRelayNetworks: boolean
  isNetworksViewActive: boolean
  onTabSelect: (chainId: number | null) => void
  onMoreClick: () => void
  // Reports which active chains overflowed the row so the parent can list them
  // at the top of the "More" menu.
  onOverflowChange?: (chainIds: number[]) => void
}

// Must match the dense TabsTrigger sizing so measured widths equal rendered ones
const MEASURE_TRIGGER_CLASSNAME =
  'flex-shrink-0 px-2 py-1.5 text-sm font-normal'

export function TokenSelectorNetworkTabs({
  activeChainIds,
  selectedChainFilter,
  selectedRelayNetwork,
  allowRelayNetworks,
  isNetworksViewActive,
  onTabSelect,
  onMoreClick,
  onOverflowChange,
}: TokenSelectorNetworkTabsProps) {
  const { visibleCount, areaCallbackRef, measureCallbackRef } =
    useChainTabsOverflow({
      chainIds: activeChainIds,
      onOverflowChange,
    })

  const visibleChainIds = activeChainIds.slice(0, visibleCount)
  const overflowChainIds = activeChainIds.slice(visibleCount)

  // The "More" tab exists when there's anything to put in it: relay networks
  // and/or chains that overflowed the row.
  const hasMoreTab = allowRelayNetworks || overflowChainIds.length > 0

  // A selected chain that overflowed is surfaced in the "More" slot (like a relay network).
  const isSelectedChainOverflowed =
    selectedChainFilter !== null &&
    overflowChainIds.includes(selectedChainFilter)

  // "More" tab is active when viewing the network list OR viewing a relay network's
  // tokens OR when the selected chain is an overflowed one.
  const isMoreActive =
    isNetworksViewActive ||
    (selectedRelayNetwork !== null &&
      selectedChainFilter === selectedRelayNetwork.id) ||
    isSelectedChainOverflowed

  const activeValue = isMoreActive
    ? 'more'
    : selectedChainFilter === null
      ? 'all'
      : String(selectedChainFilter)

  // Chain tabs show the icon only, with the name in a hover tooltip.
  const renderChainTabInner = (chainId: number) => (
    <Tooltip
      content={<NetworkDisplay layout="inline-text" chainId={chainId} />}
      position="bottom"
    >
      <NetworkLogo chainId={chainId} size="md" />
    </Tooltip>
  )

  return (
    <TabsRoot value={activeValue}>
      <TabsList variant="dense" className="-mt-2">
        {/* Pinned "Top" tab */}
        <TabsTrigger
          value="all"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onTabSelect(null)}
        >
          Top
        </TabsTrigger>
        {/* Chain tabs — a hidden row measures the natural width of every chain;
            only the ones that fit render inline, the rest go to "More". */}
        <div
          ref={areaCallbackRef}
          className="relative min-w-0 flex-1 overflow-hidden"
        >
          <div
            ref={measureCallbackRef}
            aria-hidden
            className="pointer-events-none invisible absolute left-0 top-0 flex gap-1"
          >
            {activeChainIds.map((chainId) => (
              <div key={chainId} className={MEASURE_TRIGGER_CLASSNAME}>
                <NetworkLogo chainId={chainId} size="md" />
              </div>
            ))}
          </div>
          <div className="flex gap-1">
            {visibleChainIds.map((chainId) => (
              <TabsTrigger
                key={chainId}
                value={String(chainId)}
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onTabSelect(chainId)}
              >
                {renderChainTabInner(chainId)}
              </TabsTrigger>
            ))}
          </div>
        </div>
        {/* Pinned "More" tab */}
        {hasMoreTab && (
          <TabsTrigger
            value="more"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onMoreClick}
          >
            <span className="flex items-center gap-1">
              {selectedRelayNetwork ? (
                <NetworkDisplay
                  layout="standard"
                  size="small"
                  chainId={selectedRelayNetwork.id}
                />
              ) : isSelectedChainOverflowed ? (
                // Show the selected overflow chain in the "More" slot.
                <NetworkDisplay
                  layout="standard"
                  size="small"
                  chainId={selectedChainFilter}
                />
              ) : (
                'More'
              )}
              <ChevronDown className="size-3" />
            </span>
          </TabsTrigger>
        )}
      </TabsList>
    </TabsRoot>
  )
}
